import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { motion } from 'framer-motion';
import {
  Users,
  MessageSquare,
  Send,
  Sparkles,
  Heart,
  MessageCircle,
  Mail,
  X
} from 'lucide-react';

const categoryColors = {
  general: 'bg-gray-100 text-gray-700',
  meetup: 'bg-green-100 text-green-700',
  tips: 'bg-blue-100 text-blue-700',
  questions: 'bg-purple-100 text-purple-700'
};

const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

export default function DashboardForumTab({
  user,
  forumTrips,
  selectedForumTrip,
  setSelectedForumTrip,
  forumTravelerCount,
  forumParticipants,
  showParticipants,
  setShowParticipants,
  forumPosts,
  forumLoading,
  newPost,
  setNewPost,
  postCategory,
  setPostCategory,
  expandedPost,
  postReplies,
  replyText,
  setReplyText,
  handlePostSubmit,
  handleLikePost,
  handleExpandPost,
  handleReplySubmit,
  handleDeletePost,
  handleForumDM
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-secondary" />
          Trip Discussion Forum
        </CardTitle>
        <CardDescription>
          Connect with confirmed travelers on your upcoming trip. Share tips, ask questions, and build friendships before departure!
        </CardDescription>
      </CardHeader>
      <CardContent>
        {forumTrips.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="mb-2">No trips with forum access yet</p>
            <p className="text-sm">Book a trip to join the traveler discussion forum</p>
            <Link to="/trips">
              <Button className="mt-4 bg-secondary hover:bg-secondary/90">
                Browse Adventures
              </Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Trip Selector */}
            <div className="mb-6 p-4 bg-gradient-to-r from-secondary/5 to-secondary/10 rounded-lg border border-secondary/20">
              <Label className="text-sm font-medium mb-2 block">Select Trip Forum</Label>
              <Select 
                value={selectedForumTrip ? `${selectedForumTrip.trip_id}_${selectedForumTrip.tour_date}` : ''}
                onValueChange={(val) => {
                  const trip = forumTrips.find(t => `${t.trip_id}_${t.tour_date}` === val);
                  setSelectedForumTrip(trip);
                }}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Choose a trip to view forum" />
                </SelectTrigger>
                <SelectContent>
                  {forumTrips.map(trip => (
                    <SelectItem key={`${trip.trip_id}_${trip.tour_date}`} value={`${trip.trip_id}_${trip.tour_date}`}>
                      {trip.trip_title} - {new Date(trip.tour_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedForumTrip && (
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{forumTravelerCount} travelers booked on this trip</span>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowParticipants(!showParticipants)}
                  >
                    {showParticipants ? 'Hide' : 'View'} Participants
                  </Button>
                </div>
              )}
            </div>

            {/* Participants Panel */}
            {selectedForumTrip && showParticipants && (
              <Card className="mb-6 border-secondary/30">
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4 text-secondary" />
                    Trip Participants
                  </CardTitle>
                </CardHeader>
                <CardContent className="py-2">
                  <div className="grid sm:grid-cols-2 gap-2">
                    {forumParticipants.map(participant => (
                      <div 
                        key={participant.user_id} 
                        className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-secondary/20 text-secondary text-xs">
                              {participant.name?.split(' ').map(n => n[0]).join('') || '?'}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm font-medium">
                            {participant.name}
                            {participant.is_current_user && <span className="text-xs text-muted-foreground ml-1">(you)</span>}
                          </span>
                        </div>
                        {!participant.is_current_user && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className={participant.allow_dms ? "text-secondary hover:text-secondary" : "text-muted-foreground"}
                            onClick={() => handleForumDM(participant)}
                            title={participant.allow_dms ? "Send message" : "DMs disabled"}
                          >
                            <Mail className={`h-4 w-4 ${!participant.allow_dms ? 'opacity-50' : ''}`} />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedForumTrip && (
              <>
                {/* New Post Form */}
                <form onSubmit={handlePostSubmit} className="mb-6 p-4 border rounded-lg bg-white">
                  <div className="flex items-center gap-2 mb-3">
                    <Select value={postCategory} onValueChange={setPostCategory}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="meetup">Meetup</SelectItem>
                        <SelectItem value="tips">Tips</SelectItem>
                        <SelectItem value="questions">Questions</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-muted-foreground">Category</span>
                  </div>
                  <Textarea
                    placeholder="Share something with your fellow travelers... Ask questions, suggest meetups, or share tips!"
                    value={newPost}
                    onChange={(e) => setNewPost(e.target.value)}
                    rows={3}
                    className="mb-3"
                  />
                  <Button type="submit" className="bg-secondary hover:bg-secondary/90" disabled={!newPost.trim()}>
                    <Send className="h-4 w-4 mr-2" />
                    Post to Forum
                  </Button>
                </form>

                {/* Posts List */}
                {forumLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary mx-auto" />
                  </div>
                ) : forumPosts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">Be the first to post!</p>
                    <p className="text-sm">Start a conversation with your fellow travelers</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {forumPosts.map(post => {
                      const postAuthorParticipant = forumParticipants.find(p => p.user_id === post.author_id);
                      return (
                        <motion.div 
                          key={post.id} 
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="p-4 border rounded-lg bg-white hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarFallback className="bg-secondary/20 text-secondary text-sm">
                                  {post.author_name?.split(' ').map(n => n[0]).join('') || '?'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="flex items-center gap-2">
                                  <p className="font-medium text-sm text-primary">{post.author_name}</p>
                                  <Badge variant="outline" className={`text-xs ${categoryColors[post.category] || ''}`}>
                                    {post.category}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground">{formatTimeAgo(post.created_at)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              {post.author_id !== user?.id && postAuthorParticipant && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className={postAuthorParticipant.allow_dms ? "text-secondary hover:text-secondary" : "text-muted-foreground"}
                                  onClick={() => handleForumDM(postAuthorParticipant)}
                                  title={postAuthorParticipant.allow_dms ? "Message this traveler" : "DMs disabled"}
                                >
                                  <Mail className={`h-4 w-4 ${!postAuthorParticipant.allow_dms ? 'opacity-50' : ''}`} />
                                </Button>
                              )}
                              {post.author_id === user?.id && (
                                <Button 
                                  type="button"
                                  variant="ghost" 
                                  size="sm" 
                                  className="text-muted-foreground hover:text-destructive cursor-pointer"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDeletePost(post.id);
                                  }}
                                  data-testid={`delete-post-${post.id}`}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-foreground mb-3 whitespace-pre-wrap">{post.content}</p>
                          <div className="flex items-center gap-4 text-xs">
                            <button 
                              className={`flex items-center gap-1 transition-colors ${
                                post.likes?.includes(user?.id) ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                              }`}
                              onClick={() => handleLikePost(post.id)}
                            >
                              <Heart className={`h-4 w-4 ${post.likes?.includes(user?.id) ? 'fill-current' : ''}`} /> 
                              {post.likes?.length || 0}
                            </button>
                            <button 
                              className="flex items-center gap-1 text-muted-foreground hover:text-secondary"
                              onClick={() => handleExpandPost(post.id)}
                            >
                              <MessageCircle className="h-4 w-4" /> 
                              {post.reply_count || 0} replies
                            </button>
                          </div>

                          {/* Replies Section */}
                          {expandedPost === post.id && (
                            <motion.div 
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              className="mt-4 pt-4 border-t"
                            >
                              {/* Existing Replies */}
                              {postReplies[post.id]?.length > 0 && (
                                <div className="space-y-3 mb-4">
                                  {postReplies[post.id].map(reply => (
                                    <div key={reply.id} className="pl-4 border-l-2 border-secondary/30">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Avatar className="h-6 w-6">
                                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                                            {reply.author_name?.slice(0, 2).toUpperCase() || '?'}
                                          </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm font-medium">{reply.author_name}</span>
                                        <span className="text-xs text-muted-foreground">{formatTimeAgo(reply.created_at)}</span>
                                      </div>
                                      <p className="text-sm text-muted-foreground ml-8">{reply.content}</p>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Reply Input */}
                              <div className="flex gap-2">
                                <Input
                                  placeholder="Write a reply..."
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  className="flex-1"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                      e.preventDefault();
                                      handleReplySubmit(post.id);
                                    }
                                  }}
                                />
                                <Button 
                                  size="sm" 
                                  onClick={() => handleReplySubmit(post.id)}
                                  disabled={!replyText.trim()}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </div>
                            </motion.div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
